// src/portal-app/team/EmployeeDetailScreen.js
//
// P5 — Employee Detail rewrite (portal-app-redesign-3-cramped-screens, Task 3).
//
// Was: 5 equal SegmentedControl segments across 393px (labels nearly
// collide) over cards of FieldItem grids, which rendered a bare em-dash for
// every unset field — the Personal tab alone had a run of five consecutive
// dashes (DOB -> Address -> EC Name -> EC Relationship -> EC Phone). The
// screen never said whose record it was either.
//
// Now:
//   ProfileHero   — initials/name/role·department/status pill + up to 3
//                   quick actions (Call/Message gated on phone; Edit always).
//   TabPills      — same 5 tab ids/labels as before, sized to their own
//                   text, horizontally scrollable. Keeps the `seg-*`
//                   testids via TabPills' optional per-option `testId`.
//   GroupedRows   — set fields per section, as label/value rows.
//   EmptyFieldsPrompt — every run of consecutive unset fields in a section
//                   collapses into ONE prompt naming them, Add -> that
//                   section's existing edit sheet. Never both a (partial or
//                   empty) GroupedRows card AND nothing-shown; never a
//                   prompt when nothing in the section is unset.
//
// Payroll and Schedule tabs are unchanged real components — not touched.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { employeeV2Service } from '../../services/api';
import { StatusBadge, ConfirmSheet } from '../operations/kit';
import { SurfaceStates } from '../insights/kit';
import { GlassCard } from '../shell/LiquidGlass';
import {
    TabPills, GroupedRows, EmptyFieldsPrompt, ProfileHero, ensureKitCss,
    IconPhone, IconMessage, IconEdit, FALLBACK_TONES,
} from '../shell/kit';
import { useNav, useNavChrome } from '../nav/NavStack';
import EmployeePayrollTab from './EmployeePayrollTab';
import EmployeeScheduleTab from './EmployeeScheduleTab';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.empd-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 120px;}
.empd-hero{padding-inline:16px;margin-block-end:16px;}
.empd-tabsrow{margin-block-end:16px;}

/* ---- section header (title + optional edit action) ---- */
.empd-shead{display:flex;align-items:center;justify-content:space-between;
  gap:8px;padding-inline:16px;margin-block:18px 8px;}
.empd-shead-title{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-textLight);}
.empd-block{padding-inline:16px;margin-block-end:12px;}

/* ---- Access tab card (interactive rows GroupedRows can't host) ---- */
.empd-card{padding:16px;}
.empd-field-label{font-size:11px;font-weight:600;text-transform:uppercase;
  letter-spacing:.6px;color:var(--c-mutedLight);margin-bottom:3px;}

/* ---- edit button ---- */
.empd-edit-btn{display:inline-flex;align-items:center;gap:5px;
  min-height:44px;padding:0 14px;border-radius:12px;border:none;
  background:color-mix(in srgb,var(--c-green) 10%,transparent);
  font-family:inherit;font-size:13px;font-weight:700;
  color:var(--c-green);cursor:pointer;
  transition:transform .15s ease,background .15s ease;}
.empd-edit-btn:active{transform:scale(.97);}

/* ---- PIN area ---- */
.empd-pin-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
.empd-pin-value{font-size:18px;font-weight:700;color:var(--c-ink);
  letter-spacing:4px;font-variant-numeric:tabular-nums;}
/* No reveal toggle: the API returns only has_pin, never the PIN, so there is
   nothing to reveal. The mask is a fixed six dots. */
.empd-field-hint{margin-top:6px;font-size:12px;color:var(--c-textLight);
  line-height:1.4;}

/* ---- cashier row ---- */
.empd-cashier-row{display:flex;align-items:center;gap:10px;
  margin-bottom:10px;}
.empd-cashier-label{font-size:14px;font-weight:600;color:var(--c-ink);}

/* ---- bottom sheet ---- */
.empd-sheet-backdrop{position:fixed;inset:0;z-index:1000;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.empd-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:24px 20px calc(24px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  max-height:85vh;overflow-y:auto;}
.empd-sheet-title{font-size:18px;font-weight:800;letter-spacing:-.3px;
  color:var(--c-ink);margin-bottom:16px;}
.empd-field{margin-bottom:14px;}
.empd-field-lbl{display:block;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.8px;color:var(--c-textLight);
  margin-bottom:5px;}
.empd-input{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;}
.empd-input:focus{border-color:var(--c-green);}
.empd-input-sel{appearance:none;cursor:pointer;}
.empd-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.empd-toggle-row{display:flex;align-items:center;justify-content:space-between;
  min-height:44px;padding:0 4px;}
.empd-toggle-label{font-size:14px;font-weight:600;color:var(--c-ink);}
.empd-toggle{position:relative;width:44px;height:26px;flex:none;}
.empd-toggle input{opacity:0;width:0;height:0;position:absolute;}
.empd-toggle-track{position:absolute;inset:0;border-radius:999px;
  background:color-mix(in srgb,var(--c-ink) 15%,transparent);
  transition:background .18s ease;}
.empd-toggle input:checked+.empd-toggle-track{background:var(--c-green);}
.empd-toggle-thumb{position:absolute;top:3px;left:3px;width:20px;height:20px;
  border-radius:50%;background:var(--c-white);
  transition:transform .18s ease;
  box-shadow:0 1px 4px rgba(0,0,0,.18);}
.empd-toggle input:checked~.empd-toggle-thumb{transform:translateX(18px);}
.empd-err{padding:10px 14px;border-radius:12px;margin-bottom:12px;
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-red) 25%,transparent);
  font-size:13px;font-weight:600;color:var(--c-red);}
.empd-actions{display:flex;gap:10px;margin-top:6px;}
.empd-btn-cancel{flex:1;min-height:44px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 50%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;
  transition:transform .15s ease;}
.empd-btn-cancel:active{transform:scale(.97);}
.empd-btn-save{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  transition:transform .15s ease,opacity .15s ease;}
.empd-btn-save:active{transform:scale(.97);}
.empd-btn-save:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.empd-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);
  animation:empdSpin .75s linear infinite;}
@keyframes empdSpin{to{transform:rotate(360deg);}}

/* ---- section label (inside edit sheets) ---- */
.empd-section-lbl{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-mutedLight);margin:12px 0 6px;}

/* ---- slide-up ---- */
@keyframes empdSlideUp{
  from{transform:translateY(100%);opacity:0;}
  to{transform:translateY(0);opacity:1;}
}
.empd-sheet-enter{animation:empdSlideUp 260ms ease-out both;}
@keyframes empdFadeIn{from{opacity:0;}to{opacity:1;}}
.empd-backdrop-enter{animation:empdFadeIn 200ms ease both;}

@media(prefers-reduced-motion:reduce){
  .empd-sheet-enter,.empd-backdrop-enter{animation:none;}
  .empd-spinner{animation-duration:1.5s;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'empd-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */
const LANGUAGES = [
    { value: 'en', labelKey: 'english', fallback: 'English' },
    { value: 'ar', labelKey: 'arabic', fallback: 'Arabic' },
];
const EMPLOYMENT_TYPES = [
    { value: 'full_time', labelKey: 'full_time_label', fallback: 'Full Time' },
    { value: 'part_time', labelKey: 'part_time_label', fallback: 'Part Time' },
    { value: 'contractor', labelKey: 'contractor', fallback: 'Contractor' },
];
const DEPARTMENT_OPTIONS = [
    { value: 'Kitchen', labelKey: 'dept_kitchen', fallback: 'Kitchen' },
    { value: 'Front of House', labelKey: 'dept_foh', fallback: 'Front of House' },
    { value: 'Bar', labelKey: 'dept_bar', fallback: 'Bar' },
    { value: 'Management', labelKey: 'dept_management', fallback: 'Management' },
    { value: 'Delivery', labelKey: 'dept_delivery', fallback: 'Delivery' },
    { value: 'Other', labelKey: 'dept_other', fallback: 'Other' },
];

const TAB_IDS = {
    PERSONAL: 'personal',
    EMPLOYMENT: 'employment',
    PAYROLL: 'payroll',
    SCHEDULE: 'schedule',
    ACCESS: 'access',
};

/* ------------------------------------------------------------------ */
/* SVG icons (screen-local — the section Edit button's icon; hero quick-  */
/* action icons come from the shared kit per spec)                     */
/* ------------------------------------------------------------------ */
const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);

/* EyeIcon / EyeOffIcon removed with the PIN reveal control — the PIN is no
   longer sent to the client, so there is nothing to show or hide. */

const KebabIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="5" r="1" />
        <circle cx="12" cy="12" r="1" />
        <circle cx="12" cy="19" r="1" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Identity helpers — ProfileHero initials/tone, status pill tone       */
/* ------------------------------------------------------------------ */
// Matches EmployeesScreen.js's own `initials()` convention exactly, so the
// same person gets the same two-letter mark on the list and the detail hero.
function initialsFromName(name = '') {
    return name.trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();
}

// Deterministic pick from the kit's fallback palette, keyed off the first
// character — same idea as EmployeesScreen.js's avatarVar, expressed as a
// tone name (ProfileHero colours itself from tones.js) rather than a raw var.
// Exported so EmployeesScreen.test.js can prove the two derive identically
// (Finding 7 — they used to use different, differently-ordered palettes).
export function heroToneFromName(name = '') {
    const code = name.charCodeAt(0) || 0;
    return FALLBACK_TONES[code % FALLBACK_TONES.length];
}

function statusPillTone(status) {
    return (status || '').toLowerCase() === 'active' ? 'green' : 'muted';
}

/* ------------------------------------------------------------------ */
/* Section header — title + optional Edit action.                      */
/* One header may cover several FieldBlocks below it (Personal's single */
/* edit-personal-btn spans Contact/Personal Details/Emergency Contact), */
/* so `onEdit` is only passed on the header that should carry the button. */
/* ------------------------------------------------------------------ */
function SectionHead({ title, onEdit, editTestId, editLabel, t }) {
    return (
        <div className="empd-shead">
            <span className="empd-shead-title">{title}</span>
            {onEdit && (
                <button
                    type="button"
                    className="empd-edit-btn"
                    data-testid={editTestId}
                    onClick={onEdit}
                    aria-label={editLabel}
                >
                    <EditIcon />
                    {t('edit', {}, 'Edit')}
                </button>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* FieldBlock — the collapse rule.                                     */
/* Partitions `fields` into set/unset. Set ones render as GroupedRows; */
/* every unset one collapses into ONE EmptyFieldsPrompt whose Add opens */
/* `onAdd` (the section's existing edit sheet). All-unset -> prompt      */
/* only, no empty card above it. None-unset -> no prompt. `extraRows`   */
/* (Employment's already-conditional Termination Reason) is appended    */
/* verbatim and never participates in the unset partition.              */
/* ------------------------------------------------------------------ */
function FieldBlock({ fields, onAdd, extraRows }) {
    const set = fields.filter((f) => f.value);
    const unset = fields.filter((f) => !f.value);
    const rows = set.map((f) => ({ id: f.key, label: f.label, value: f.value }));
    if (Array.isArray(extraRows)) rows.push(...extraRows);

    if (rows.length === 0 && unset.length === 0) return null;

    return (
        <div className="empd-block">
            {rows.length > 0 && <GroupedRows rows={rows} />}
            {unset.length > 0 && (
                <EmptyFieldsPrompt fields={unset.map((f) => f.label)} onAdd={onAdd} />
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Sheet wrapper (glass bottom sheet)                                  */
/* ------------------------------------------------------------------ */
function EditSheet({ testId, open, onClose, title, children }) {
    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="empd-sheet-backdrop empd-backdrop-enter"
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <GlassCard
                veil
                className="empd-sheet empd-sheet-enter"
                data-testid={testId}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="empd-sheet-title">{title}</div>
                {children}
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* PersonalTab                                                          */
/* ------------------------------------------------------------------ */
function PersonalTab({ emp, onReload, t }) {
    const p = emp.personal || {};
    const [editOpen, setEditOpen] = useState(false);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const openEdit = () => {
        setForm({
            phone: p.phone || '',
            email: p.email || '',
            preferred_language: p.preferred_language || 'en',
            date_of_birth: p.date_of_birth || '',
            address: p.address || '',
            emergency_contact_name: p.emergency_contact_name || '',
            emergency_contact_relationship: p.emergency_contact_relationship || '',
            emergency_contact_phone: p.emergency_contact_phone || '',
        });
        setError('');
        setEditOpen(true);
    };

    const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            await employeeV2Service.updatePersonal(emp.employee_id, {
                phone: form.phone || null,
                email: form.email || null,
                preferred_language: form.preferred_language || 'en',
                date_of_birth: form.date_of_birth || null,
                address: form.address || null,
                emergency_contact_name: form.emergency_contact_name || null,
                emergency_contact_relationship: form.emergency_contact_relationship || null,
                emergency_contact_phone: form.emergency_contact_phone || null,
            });
            setSaving(false);
            setEditOpen(false);
            await onReload();
        } catch (err) {
            setSaving(false);
            setError(err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed'));
        }
    };

    const langLabel = (langValue) => {
        const lang = LANGUAGES.find((l) => l.value === langValue);
        return lang ? t(lang.labelKey, {}, lang.fallback) : langValue;
    };

    const contactFields = [
        { key: 'phone', label: t('phone', {}, 'Phone'), value: p.phone },
        { key: 'email', label: t('email', {}, 'Email'), value: p.email },
        {
            key: 'lang',
            label: t('preferred_language', {}, 'Language'),
            value: p.preferred_language ? langLabel(p.preferred_language) : null,
        },
    ];
    const personalFields = [
        { key: 'dob', label: t('date_of_birth', {}, 'Date of Birth'), value: p.date_of_birth },
        { key: 'address', label: t('address', {}, 'Address'), value: p.address },
    ];
    const ecFields = [
        { key: 'ec_name', label: t('name', {}, 'Name'), value: p.emergency_contact_name },
        { key: 'ec_rel', label: t('relationship', {}, 'Relationship'), value: p.emergency_contact_relationship },
        { key: 'ec_phone', label: t('phone', {}, 'Phone'), value: p.emergency_contact_phone },
    ];

    return (
        <div>
            <SectionHead
                title={t('contact_info', {}, 'Contact Info')}
                onEdit={openEdit}
                editTestId="edit-personal-btn"
                editLabel={t('edit_personal_info', {}, 'Edit personal info')}
                t={t}
            />
            <FieldBlock fields={contactFields} onAdd={openEdit} />

            <SectionHead title={t('personal_info', {}, 'Personal Details')} t={t} />
            <FieldBlock fields={personalFields} onAdd={openEdit} />

            <SectionHead title={t('emergency_contact', {}, 'Emergency Contact')} t={t} />
            <FieldBlock fields={ecFields} onAdd={openEdit} />

            {/* Edit sheet */}
            <EditSheet
                testId="personal-edit-sheet"
                open={editOpen}
                onClose={() => setEditOpen(false)}
                title={t('edit_personal_info', {}, 'Edit Personal Info')}
            >
                {error && <div className="empd-err" data-testid="personal-err">{error}</div>}

                <div className="empd-section-lbl">{t('contact_info', {}, 'Contact Info')}</div>
                <div className="empd-grid-2">
                    <div className="empd-field">
                        <label className="empd-field-lbl">{t('phone', {}, 'Phone')}</label>
                        <input
                            className="empd-input"
                            data-testid="personal-phone"
                            type="tel"
                            value={form.phone || ''}
                            onChange={(e) => set('phone', e.target.value)}
                        />
                    </div>
                    <div className="empd-field">
                        <label className="empd-field-lbl">{t('email', {}, 'Email')}</label>
                        <input
                            className="empd-input"
                            data-testid="personal-email"
                            type="email"
                            value={form.email || ''}
                            onChange={(e) => set('email', e.target.value)}
                        />
                    </div>
                </div>

                <div className="empd-field">
                    <label className="empd-field-lbl">{t('preferred_language', {}, 'Preferred Language')}</label>
                    <select
                        className="empd-input empd-input-sel"
                        data-testid="personal-language"
                        value={form.preferred_language || 'en'}
                        onChange={(e) => set('preferred_language', e.target.value)}
                    >
                        {LANGUAGES.map((l) => (
                            <option key={l.value} value={l.value}>
                                {t(l.labelKey, {}, l.fallback)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="empd-section-lbl">{t('personal_info', {}, 'Personal Details')}</div>
                <div className="empd-grid-2">
                    <div className="empd-field">
                        <label className="empd-field-lbl">{t('date_of_birth', {}, 'Date of Birth')}</label>
                        <input
                            className="empd-input"
                            data-testid="personal-dob"
                            type="date"
                            value={form.date_of_birth || ''}
                            onChange={(e) => set('date_of_birth', e.target.value)}
                        />
                    </div>
                    <div className="empd-field">
                        <label className="empd-field-lbl">{t('address', {}, 'Address')}</label>
                        <input
                            className="empd-input"
                            data-testid="personal-address"
                            type="text"
                            value={form.address || ''}
                            onChange={(e) => set('address', e.target.value)}
                        />
                    </div>
                </div>

                <div className="empd-section-lbl">{t('emergency_contact', {}, 'Emergency Contact')}</div>
                <div className="empd-field">
                    <label className="empd-field-lbl">{t('name', {}, 'Name')}</label>
                    <input
                        className="empd-input"
                        data-testid="personal-ec-name"
                        type="text"
                        value={form.emergency_contact_name || ''}
                        onChange={(e) => set('emergency_contact_name', e.target.value)}
                    />
                </div>
                <div className="empd-grid-2">
                    <div className="empd-field">
                        <label className="empd-field-lbl">{t('relationship', {}, 'Relationship')}</label>
                        <input
                            className="empd-input"
                            data-testid="personal-ec-relationship"
                            type="text"
                            value={form.emergency_contact_relationship || ''}
                            onChange={(e) => set('emergency_contact_relationship', e.target.value)}
                        />
                    </div>
                    <div className="empd-field">
                        <label className="empd-field-lbl">{t('phone', {}, 'Phone')}</label>
                        <input
                            className="empd-input"
                            data-testid="personal-ec-phone"
                            type="tel"
                            value={form.emergency_contact_phone || ''}
                            onChange={(e) => set('emergency_contact_phone', e.target.value)}
                        />
                    </div>
                </div>

                <div className="empd-actions">
                    <button type="button" className="empd-btn-cancel" onClick={() => setEditOpen(false)}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="empd-btn-save"
                        data-testid="personal-save-btn"
                        disabled={saving}
                        onClick={handleSave}
                    >
                        {saving && <span className="empd-spinner" aria-hidden="true" />}
                        {saving ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </EditSheet>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* EmploymentTab                                                        */
/* ------------------------------------------------------------------ */
function EmploymentTab({ emp, onReload, t }) {
    const em = emp.employment || {};
    const [editEmpOpen, setEditEmpOpen] = useState(false);
    const [editCoreOpen, setEditCoreOpen] = useState(false);
    const [empForm, setEmpForm] = useState({});
    const [coreForm, setCoreForm] = useState({});
    const [savingEmp, setSavingEmp] = useState(false);
    const [savingCore, setSavingCore] = useState(false);
    const [errEmp, setErrEmp] = useState('');
    const [errCore, setErrCore] = useState('');

    const openEditEmp = () => {
        setEmpForm({
            employment_type: em.employment_type || 'full_time',
            start_date: em.start_date || '',
            end_date: em.end_date || '',
            termination_reason: em.termination_reason || '',
        });
        setErrEmp('');
        setEditEmpOpen(true);
    };

    const openEditCore = () => {
        setCoreForm({ role: emp.role || '', department: emp.department || '' });
        setErrCore('');
        setEditCoreOpen(true);
    };

    const setEmp = (field, value) => setEmpForm((f) => ({ ...f, [field]: value }));
    const setCore = (field, value) => setCoreForm((f) => ({ ...f, [field]: value }));

    const handleSaveEmp = async () => {
        setSavingEmp(true);
        setErrEmp('');
        try {
            await employeeV2Service.updateEmployment(emp.employee_id, {
                employment_type: empForm.employment_type || null,
                start_date: empForm.start_date || null,
                end_date: empForm.end_date || null,
                termination_reason: empForm.termination_reason || null,
            });
            setSavingEmp(false);
            setEditEmpOpen(false);
            await onReload();
        } catch (err) {
            setSavingEmp(false);
            setErrEmp(err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed'));
        }
    };

    const handleSaveCore = async () => {
        setSavingCore(true);
        setErrCore('');
        try {
            await employeeV2Service.updateCore(emp.employee_id, {
                role: coreForm.role || null,
                department: coreForm.department || null,
            });
            setSavingCore(false);
            setEditCoreOpen(false);
            await onReload();
        } catch (err) {
            setSavingCore(false);
            setErrCore(err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed'));
        }
    };

    const empTypeLabel = (val) => {
        const et = EMPLOYMENT_TYPES.find((e) => e.value === val);
        return et ? t(et.labelKey, {}, et.fallback) : val;
    };

    const coreFields = [
        { key: 'role', label: t('role', {}, 'Role'), value: emp.role },
        { key: 'department', label: t('department', {}, 'Department'), value: emp.department },
    ];
    const employmentFields = [
        {
            key: 'type',
            label: t('employment_type', {}, 'Employment Type'),
            value: em.employment_type ? empTypeLabel(em.employment_type) : null,
        },
        { key: 'start', label: t('start_date', {}, 'Start Date'), value: em.start_date },
    ];
    // End Date and Termination Reason are both facts that are legitimately
    // absent for a currently-employed person — their absence isn't a gap to
    // nag the manager to fill in (unlike, say, an unset phone number). Both
    // are conditional: appended verbatim only once set, and never counted as
    // "unset" in the Employment Details collapse.
    const employmentExtras = [
        ...(em.end_date ? [{ id: 'end', label: t('end_date', {}, 'End Date'), value: em.end_date }] : []),
        ...(em.termination_reason
            ? [{ id: 'term', label: t('termination_reason', {}, 'Termination Reason'), value: em.termination_reason }]
            : []),
    ];
    const employmentExtraRows = employmentExtras.length > 0 ? employmentExtras : undefined;

    return (
        <div>
            <SectionHead
                title={t('role_department', {}, 'Role & Department')}
                onEdit={openEditCore}
                editTestId="edit-core-btn"
                editLabel={t('edit_role_department', {}, 'Edit role and department')}
                t={t}
            />
            <FieldBlock fields={coreFields} onAdd={openEditCore} />

            <SectionHead
                title={t('employment_details', {}, 'Employment Details')}
                onEdit={openEditEmp}
                editTestId="edit-employment-btn"
                editLabel={t('edit_employment', {}, 'Edit employment details')}
                t={t}
            />
            <FieldBlock fields={employmentFields} onAdd={openEditEmp} extraRows={employmentExtraRows} />

            {/* Edit role/dept sheet */}
            <EditSheet
                testId="core-edit-sheet"
                open={editCoreOpen}
                onClose={() => setEditCoreOpen(false)}
                title={t('edit_role_department', {}, 'Edit Role & Department')}
            >
                {errCore && <div className="empd-err">{errCore}</div>}

                <div className="empd-field">
                    <label className="empd-field-lbl">{t('role', {}, 'Role')}</label>
                    <input
                        className="empd-input"
                        data-testid="core-role"
                        type="text"
                        value={coreForm.role || ''}
                        onChange={(e) => setCore('role', e.target.value)}
                    />
                </div>
                <div className="empd-field">
                    <label className="empd-field-lbl">{t('department', {}, 'Department')}</label>
                    <select
                        className="empd-input empd-input-sel"
                        data-testid="core-department"
                        value={coreForm.department || ''}
                        onChange={(e) => setCore('department', e.target.value)}
                    >
                        <option value="">{t('select_department', {}, 'Select department')}</option>
                        {DEPARTMENT_OPTIONS.map((d) => (
                            <option key={d.value} value={d.value}>
                                {t(d.labelKey, {}, d.fallback)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="empd-actions">
                    <button type="button" className="empd-btn-cancel" onClick={() => setEditCoreOpen(false)}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="empd-btn-save"
                        data-testid="core-save-btn"
                        disabled={savingCore}
                        onClick={handleSaveCore}
                    >
                        {savingCore && <span className="empd-spinner" aria-hidden="true" />}
                        {savingCore ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </EditSheet>

            {/* Edit employment sheet */}
            <EditSheet
                testId="employment-edit-sheet"
                open={editEmpOpen}
                onClose={() => setEditEmpOpen(false)}
                title={t('edit_employment', {}, 'Edit Employment')}
            >
                {errEmp && <div className="empd-err">{errEmp}</div>}

                <div className="empd-field">
                    <label className="empd-field-lbl">{t('employment_type', {}, 'Employment Type')}</label>
                    <select
                        className="empd-input empd-input-sel"
                        data-testid="emp-type"
                        value={empForm.employment_type || ''}
                        onChange={(e) => setEmp('employment_type', e.target.value)}
                    >
                        {EMPLOYMENT_TYPES.map((et) => (
                            <option key={et.value} value={et.value}>
                                {t(et.labelKey, {}, et.fallback)}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="empd-grid-2">
                    <div className="empd-field">
                        <label className="empd-field-lbl">{t('start_date', {}, 'Start Date')}</label>
                        <input
                            className="empd-input"
                            data-testid="emp-start-date"
                            type="date"
                            value={empForm.start_date || ''}
                            onChange={(e) => setEmp('start_date', e.target.value)}
                        />
                    </div>
                    <div className="empd-field">
                        <label className="empd-field-lbl">{t('end_date', {}, 'End Date')}</label>
                        <input
                            className="empd-input"
                            data-testid="emp-end-date"
                            type="date"
                            value={empForm.end_date || ''}
                            onChange={(e) => setEmp('end_date', e.target.value)}
                        />
                    </div>
                </div>
                <div className="empd-field">
                    <label className="empd-field-lbl">{t('termination_reason', {}, 'Termination Reason')}</label>
                    <input
                        className="empd-input"
                        data-testid="emp-termination-reason"
                        type="text"
                        value={empForm.termination_reason || ''}
                        onChange={(e) => setEmp('termination_reason', e.target.value)}
                    />
                </div>

                <div className="empd-actions">
                    <button type="button" className="empd-btn-cancel" onClick={() => setEditEmpOpen(false)}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="empd-btn-save"
                        data-testid="employment-save-btn"
                        disabled={savingEmp}
                        onClick={handleSaveEmp}
                    >
                        {savingEmp && <span className="empd-spinner" aria-hidden="true" />}
                        {savingEmp ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </EditSheet>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* AccessTab                                                            */
/* ------------------------------------------------------------------ */
/* SECURITY: the API no longer returns the PIN — only `access.has_pin`, a
   boolean. So this tab can show *that* a PIN is set, never what it is, and the
   edit form starts blank. A blank PIN box therefore means "leave unchanged",
   which is why handleSave omits the key entirely rather than sending null:
   `update_employee_access` uses exclude_unset, so an omitted key preserves the
   stored PIN while an explicit null would wipe it and lock the employee out of
   the POS. Backend contract pinned by
   backend/tests/test_employee_pin_not_in_responses.py. */
function AccessTab({ emp, onReload, t }) {
    const ac = emp.access || {};
    const [editOpen, setEditOpen] = useState(false);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const openEdit = () => {
        setForm({
            is_cashier: !!ac.is_cashier,
            pin_code: '',
            // Seeded from the employee's CURRENT level — defaulting to 'staff'
            // here would silently demote a manager on the next save.
            system_role: emp.system_role || 'staff',
        });
        setError('');
        setEditOpen(true);
    };

    const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const payload = { is_cashier: !!form.is_cashier };
            // Only send the PIN when the manager actually typed a new one.
            if (form.pin_code) payload.pin_code = form.pin_code;
            // Only send system_role when it actually CHANGED. The backend
            // treats re-sending the stored value as a no-op, but staying
            // silent keeps ordinary access edits (toggling cashier, resetting
            // a PIN) working for callers who lack portal:roles.
            if ((form.system_role || 'staff') !== (emp.system_role || 'staff')) {
                payload.system_role = form.system_role;
            }
            await employeeV2Service.updateAccess(emp.employee_id, payload);
            setSaving(false);
            setEditOpen(false);
            await onReload();
        } catch (err) {
            setSaving(false);
            setError(err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed'));
        }
    };

    return (
        <div>
            <SectionHead
                title={t('pos_access', {}, 'POS Access')}
                onEdit={openEdit}
                editTestId="edit-access-btn"
                editLabel={t('edit_access', {}, 'Edit access')}
                t={t}
            />

            <div className="empd-block">
                <GlassCard veil className="empd-card">
                    {/* Cashier indicator — a real "Not a cashier" state, not an
                        em-dash-capable field, so it never enters the collapse rule. */}
                    <div className="empd-cashier-row">
                        <span className="empd-cashier-label">{t('cashier_access', {}, 'Cashier Access')}</span>
                        <StatusBadge
                            status={ac.is_cashier ? 'cashier' : 'no-cashier'}
                            label={ac.is_cashier
                                ? t('cashier', {}, 'Cashier')
                                : t('not_a_cashier', {}, 'Not a cashier')}
                            tone={ac.is_cashier ? 'blue' : 'muted'}
                        />
                    </div>

                    {/* PIN display — only rendered once a PIN exists; an unset PIN
                        is a prompt (below), never a bare '—' dash. The value is
                        never sent to the client, so this is a fixed-width mask
                        with no reveal control. */}
                    <div>
                        <div className="empd-field-label">{t('pos_access_level', {}, 'POS access level')}</div>
                        <div className="empd-pin-row">
                            <span data-testid="system-role-value">
                                {emp.system_role === 'manager'
                                    ? t('pos_access_manager', {}, 'Manager')
                                    : t('pos_access_staff', {}, 'Staff')}
                            </span>
                        </div>
                    </div>

                    {ac.has_pin && (
                        <div>
                            <div className="empd-field-label">{t('pin_code', {}, 'PIN Code')}</div>
                            <div className="empd-pin-row">
                                <span
                                    className="empd-pin-value"
                                    data-testid="pin-masked"
                                    aria-label={t('pin_set', {}, 'PIN set')}
                                >
                                    ••••••
                                </span>
                            </div>
                        </div>
                    )}
                </GlassCard>
            </div>

            {!ac.has_pin && (
                <div className="empd-block">
                    <EmptyFieldsPrompt fields={[t('pin_code', {}, 'PIN Code')]} onAdd={openEdit} />
                </div>
            )}

            {/* Edit access sheet */}
            <EditSheet
                testId="access-edit-sheet"
                open={editOpen}
                onClose={() => setEditOpen(false)}
                title={t('edit_access', {}, 'Edit Access')}
            >
                {error && <div className="empd-err">{error}</div>}

                <div className="empd-toggle-row">
                    <span className="empd-toggle-label">{t('cashier_access', {}, 'Cashier Access')}</span>
                    <label className="empd-toggle" aria-label={t('cashier_access', {}, 'Cashier Access')}>
                        <input
                            type="checkbox"
                            data-testid="access-is-cashier"
                            checked={!!form.is_cashier}
                            onChange={(e) => set('is_cashier', e.target.checked)}
                        />
                        <span className="empd-toggle-track" />
                        <span className="empd-toggle-thumb" />
                    </label>
                </div>

                <div className="empd-field">
                    <label className="empd-field-lbl">{t('pos_access_level', {}, 'POS access level')}</label>
                    <select
                        className="empd-input empd-input-sel"
                        data-testid="access-system-role"
                        value={form.system_role || 'staff'}
                        onChange={(e) => set('system_role', e.target.value)}
                    >
                        <option value="staff">{t('pos_access_staff', {}, 'Staff')}</option>
                        <option value="manager">{t('pos_access_manager', {}, 'Manager')}</option>
                    </select>
                    <div className="empd-field-hint">
                        {form.system_role === 'manager'
                            ? t('pos_access_manager_hint', {}, 'Can also configure the device and use manager functions.')
                            : t('pos_access_staff_hint', {}, 'Can take orders and payments on the till.')}
                    </div>
                </div>

                <div className="empd-field">
                    <label className="empd-field-lbl">{t('pin_code', {}, 'PIN Code')}</label>
                    <input
                        className="empd-input"
                        data-testid="access-pin"
                        type="password"
                        inputMode="numeric"
                        value={form.pin_code || ''}
                        placeholder="••••••"
                        maxLength={6}
                        onChange={(e) => set('pin_code', e.target.value)}
                    />
                    {ac.has_pin && (
                        <div className="empd-field-hint" data-testid="pin-unchanged-hint">
                            {t('pin_blank_keeps_current', {}, 'Leave blank to keep the current PIN')}
                        </div>
                    )}
                </div>

                <div className="empd-actions">
                    <button type="button" className="empd-btn-cancel" onClick={() => setEditOpen(false)}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="empd-btn-save"
                        data-testid="access-save-btn"
                        disabled={saving}
                        onClick={handleSave}
                    >
                        {saving && <span className="empd-spinner" aria-hidden="true" />}
                        {saving ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </EditSheet>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* PayrollTab wrapper — real implementation, not touched                */
/* ------------------------------------------------------------------ */
function PayrollTab({ employeeId, currency }) {
    return (
        <EmployeePayrollTab
            employeeId={employeeId}
            currency={currency}
        />
    );
}

/* ------------------------------------------------------------------ */
/* ScheduleTab wrapper — real implementation, not touched               */
/* ------------------------------------------------------------------ */
function ScheduleTab({ employeeId, currency }) {
    return (
        <EmployeeScheduleTab
            employeeId={employeeId}
            currency={currency}
        />
    );
}

/* ------------------------------------------------------------------ */
/* EmployeeDetailScreen                                                 */
/* ------------------------------------------------------------------ */
export default function EmployeeDetailScreen({ employeeId }) {
    ensureCss();
    ensureKitCss();
    const { t, isRTL } = useLanguage();
    const { getCurrency } = useAuth();
    const currency = getCurrency ? getCurrency() : '£';
    const { pop } = useNav();

    const [emp, setEmp] = useState(null);
    const [status, setStatus] = useState('loading');
    const [activeTab, setActiveTab] = useState(TAB_IDS.PERSONAL);
    const [deactivateOpen, setDeactivateOpen] = useState(false);
    const [deactivateReason, setDeactivateReason] = useState('');
    const [deactivating, setDeactivating] = useState(false);

    const fetchRef = useRef(0);

    const load = useCallback(async () => {
        const id = ++fetchRef.current;
        setStatus('loading');
        try {
            const data = await employeeV2Service.getEmployeeDetail(employeeId);
            if (id !== fetchRef.current) return;
            setEmp(data);
            setStatus('ready');
        } catch {
            if (id !== fetchRef.current) return;
            setStatus('error');
        }
    }, [employeeId]);

    useEffect(() => { load(); }, [load]);

    // ── Nav chrome: subtitle = status, trailing = deactivate kebab ──────
    useNavChrome(
        emp
            ? {
                subtitle: emp.status || '',
                actions: [
                    {
                        id: 'deactivate',
                        label: t('deactivate_employee', {}, 'Deactivate Employee'),
                        icon: <KebabIcon />,
                        onPress: () => setDeactivateOpen(true),
                    },
                ],
            }
            : null,
        [emp, t],
    );

    // ── Deactivate handler ───────────────────────────────────────────────
    const handleDeactivate = async () => {
        setDeactivating(true);
        try {
            await employeeV2Service.softDelete(employeeId, deactivateReason);
            setDeactivating(false);
            setDeactivateOpen(false);
            pop();
        } catch {
            setDeactivating(false);
        }
    };

    // ── Section tabs (TabPills — replaces SegmentedControl; keeps the      ──
    //    existing seg-* testids via TabPills' optional per-option testId) ──
    const segmentOptions = [
        { id: TAB_IDS.PERSONAL, label: t('tab_personal', {}, 'Personal'), testId: 'seg-personal' },
        { id: TAB_IDS.EMPLOYMENT, label: t('tab_employment', {}, 'Employment'), testId: 'seg-employment' },
        { id: TAB_IDS.PAYROLL, label: t('tab_payroll', {}, 'Payroll'), testId: 'seg-payroll' },
        { id: TAB_IDS.SCHEDULE, label: t('tab_schedule', {}, 'Schedule'), testId: 'seg-schedule' },
        { id: TAB_IDS.ACCESS, label: t('tab_access', {}, 'Access'), testId: 'seg-access' },
    ];

    // ── ProfileHero content ──────────────────────────────────────────────
    const p = emp?.personal || {};
    const heroSubtitle = emp ? ([emp.role, emp.department].filter(Boolean).join(' · ') || undefined) : undefined;
    const heroPill = emp?.status ? { label: emp.status, tone: statusPillTone(emp.status) } : undefined;

    // Call/Message share the same underlying field (phone) and are omitted
    // together when it's unset. Edit always shows — it isn't gated on a
    // field, it takes the manager to the Personal tab where fields (and the
    // Edit button that owns this mutation) live. Capped at 3 by ProfileHero
    // itself, but never exceeds 3 here either (Call + Message + Edit).
    const heroActions = emp
        ? [
            ...(p.phone
                ? [
                    {
                        id: 'call',
                        label: t('call', {}, 'Call'),
                        icon: <IconPhone />,
                        onPress: () => { window.location.href = `tel:${p.phone}`; },
                    },
                    {
                        id: 'message',
                        label: t('message', {}, 'Message'),
                        icon: <IconMessage />,
                        onPress: () => { window.location.href = `sms:${p.phone}`; },
                    },
                ]
                : []),
            {
                id: 'edit',
                label: t('edit', {}, 'Edit'),
                icon: <IconEdit />,
                onPress: () => setActiveTab(TAB_IDS.PERSONAL),
            },
        ]
        : [];

    return (
        <div
            className="empd-wrap"
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="employee-detail-screen"
        >
            <SurfaceStates status={status} onRetry={load}>
                {emp && (
                    <>
                        {/* Identity block — a detail screen without one could be
                            about anyone; this is what makes it about a person. */}
                        <div className="empd-hero">
                            <ProfileHero
                                initials={initialsFromName(emp.name || '')}
                                tone={heroToneFromName(emp.name || '')}
                                name={emp.name}
                                subtitle={heroSubtitle}
                                pill={heroPill}
                                actions={heroActions}
                            />
                        </div>

                        {/* Section tabs */}
                        <div className="empd-tabsrow">
                            <TabPills
                                options={segmentOptions}
                                value={activeTab}
                                onChange={setActiveTab}
                                ariaLabel={t('employee_sections', {}, 'Employee sections')}
                            />
                        </div>

                        {/* Tab content */}
                        <div>
                            {activeTab === TAB_IDS.PERSONAL && (
                                <PersonalTab emp={emp} onReload={load} t={t} />
                            )}
                            {activeTab === TAB_IDS.EMPLOYMENT && (
                                <EmploymentTab emp={emp} onReload={load} t={t} />
                            )}
                            {activeTab === TAB_IDS.PAYROLL && (
                                <PayrollTab employeeId={employeeId} currency={currency} />
                            )}
                            {activeTab === TAB_IDS.SCHEDULE && (
                                <ScheduleTab employeeId={employeeId} currency={currency} />
                            )}
                            {activeTab === TAB_IDS.ACCESS && (
                                <AccessTab emp={emp} onReload={load} t={t} />
                            )}
                        </div>

                        {/* Deactivate stays behind the nav-bar kebab (useNavChrome
                            action above), not a ProfileHero quick action: the hero's
                            3 slots are already Call/Message/Edit, and a destructive
                            action sitting next to Call would be one careless tap
                            away from firing. There used to be a second, sr-only
                            copy of this button here purely so the test suite could
                            reach the handler — but clip:rect hides a control
                            visually while leaving it in tab order and the a11y
                            tree, so a keyboard user could tab onto an invisible
                            button with no focus ring, and a screen reader announced
                            "Deactivate Employee" twice. Removed: the real kebab
                            action's onPress is exercised directly in tests via the
                            useNavChrome mock instead. */}

                        {/* Deactivate confirm sheet */}
                        <ConfirmSheet
                            open={deactivateOpen}
                            onClose={() => {
                                setDeactivateOpen(false);
                                setDeactivateReason('');
                            }}
                            onConfirm={handleDeactivate}
                            title={t('deactivate_employee', {}, 'Deactivate Employee')}
                            message={t(
                                'deactivate_employee_msg',
                                {},
                                'This will deactivate the employee POS access. Reactivate them later.',
                            )}
                            confirmLabel={t('deactivate', {}, 'Deactivate')}
                            destructive
                            reasonLabel={t('reason_optional', {}, 'Reason (optional)')}
                            reasonValue={deactivateReason}
                            onReasonChange={setDeactivateReason}
                            loading={deactivating}
                        />
                    </>
                )}
            </SurfaceStates>
        </div>
    );
}
